import React, { useContext } from 'react'

import SavedItemRemoveButton from '../ItemCard/Common/SavedItemRemoveButton'
import FlightItemCardHeading from '../ItemCard/Flight/FlightItemCardHeading'
import type { ItemCardVariant } from '../ItemCard/types'

import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import {
  fareTypeLabelMap,
  fareCabinLabelMap,
  FlightsFareTypes,
} from 'constants/flight'
import {
  formatOccupantsShort,
  sumUpOccupancies,
} from 'lib/offer/occupancyUtils'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import AttachedDocument from 'tripPlanner/components/Documents/AttachedDocument'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import { useLowestFlightPrice } from 'tripPlanner/hooks/api/flights'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import type { LEFlightItem } from 'tripPlanner/types/tripItem'
import { formatShortDateRange } from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'

export interface Props {
  item: LEFlightItem
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
  onFlightSearchClick: () => void
}

export default function FlightSearchTileContent({
  item,
  tripNameLabel,
  variant,
  onFlightSearchClick,
}: Props) {
  const {
    originAirportCode,
    destinationAirportCode,
    savedItemData: {
      currencyCode,
      regionCode,
      adultPrice,
      fareClass,
      fareType,
      totalPrice,
      travellerCounts,
    },
  } = item

  const canEdit = useCanEditTrip()
  const effectiveCurrencyCode = currencyCode
  const { data: price, isLoading: priceLoading } = useLowestFlightPrice({
    originAirportCode,
    destinationAirportCode,
    currencyCode: effectiveCurrencyCode,
    regionCode,
    shouldQuery:
      !adultPrice && fareType === FlightsFareTypes.RETURN,
  })

  const minAdultPrice = adultPrice ?? price
  const showPrice = !priceLoading && !!minAdultPrice

  const travellerCount: App.Occupants = travellerCounts ?? {
    adults: 0,
  }
  const showTotal = totalPrice && sumUpOccupancies([travellerCount]) > 1
  const fareTypeLabel = fareTypeLabelMap[fareType]
  const fareClassLabel = fareCabinLabelMap[fareClass as keyof typeof fareCabinLabelMap]

  const description = [
    formatShortDateRange(item),
    fareClassLabel,
    fareTypeLabel,
    formatOccupantsShort(travellerCount),
  ]
    .filter(Boolean)
    .join(' · ')

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  const source = useTripClientSource()
  const flightSearchUrl = addQueryParamsToPath(
    buildStandaloneFlightSearchURL(item.savedItemData),
    {
      source,
    },
  )

  return (
    <>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
      >
        <BodyTextBlock variant="small" startIcon={<LinePlaneFlyIcon />}>
          Flight
        </BodyTextBlock>
        {canEdit && <SavedItemRemoveButton item={item} />}
      </Group>
      <VerticalSpacer gap={4}>
        <FlightItemCardHeading item={item} />
        <BodyText variant="medium">{description}</BodyText>
      </VerticalSpacer>
      <VerticalSpacer gap={8}>
        {tripNameLabel}

        {!item.isSuggested && <>
          {attachedDocuments.map((document) => (
            <AttachedDocument key={document.filename} document={document} item={item} />
          ))}
          {documentContext && canEdit && (
            <AssistChip
              size="small"
              onClick={() => documentContext.onRequestAddDocument(item.id)}
              variant="filled"
              startIcon={<LinePaperclipIcon />}
            >
              Attach document
            </AssistChip>
          )}
        </>}

        <Group
          direction="horizontal"
          horizontalAlign={showPrice ? 'space-between' : 'end'}
          verticalAlign="end"
        >
          {showPrice && (
            <div>
              <Caption variant="medium">{fareTypeLabel} from</Caption>
              <div>
                <Heading as="span" variant="heading6">
                  <FormatCurrency
                    value={minAdultPrice}
                    currency={effectiveCurrencyCode}
                    format="dynamicDollar"
                  />
                  *
                </Heading>
                <BodyText as="span" variant="small">
                  {' '}
                  /adult
                </BodyText>
                {showTotal && (
                  <Caption variant="medium" colour="neutral-three">
                    Total from <FormatCurrency value={totalPrice} />
                  </Caption>
                )}
              </div>
            </div>
          )}
          {canEdit && (
            <TextButton
              kind={variant === 'drawer' ? 'primary' : 'secondary'}
              to={flightSearchUrl}
              onClick={onFlightSearchClick}
            >
              Search flights
            </TextButton>
          )}
          {!canEdit && (
            <TextButton
              kind="secondary"
              to={flightSearchUrl}
              onClick={onFlightSearchClick}
            >
              Find flights
            </TextButton>
          )}
        </Group>
        {showPrice && (
          <Caption colour="neutral-four" variant="medium">
            *Price and availability are subject to change.
          </Caption>
        )}
      </VerticalSpacer>
    </>
  )
}
